/**
 * Labstep
 *
 * @module components/Entity/Action/Delete/Bulk
 * @desc Delete entities in bulk
 */

import { useHasAccessMultiple } from 'labstep-web/components/Entity/Can/hooks';
import { Action } from 'labstep-web/components/Entity/Can/types';
import { ActionComponentBulk } from 'labstep-web/core/Action/Component/Bulk';
import type { Entity } from 'labstep-web/models/entity.model';
import {
  capitalize,
  getHumanReadableEntityName,
} from 'labstep-web/services/i18n.service';
import type React from 'react';
import { EntityActionDeleteComponent } from '..';
import type { EntityActionDeleteBulkProps } from './types';

export const EntityActionDeleteBulk: React.FC<
  EntityActionDeleteBulkProps
> = (props) => {
  const {
    entities,
    options = {},
    header,
    entityName,
    actionText,
    confirmMessage,
    confirmButtonContent,
    actionComponentProps,
  } = props;

  const entityIdsAccessObject = useHasAccessMultiple(
    entityName,
    entities.filter((e) => e.id).map((e) => e.id),
    Action.delete,
  );

  const entitiesWithPermission = entities
    ? entities.filter(
        (e: Entity) => entityIdsAccessObject[e.id as number],
      )
    : [];

  if (
    !entities ||
    entities.length === 0 ||
    entitiesWithPermission.length === 0
  ) {
    return null;
  }

  const id = entitiesWithPermission.map((e) => e.id);
  const firstEntity = entities[0];

  const isActive = firstEntity.deleted_at === null;
  const name = getHumanReadableEntityName(
    firstEntity.entityName,
    true,
  );

  const actionLabel = actionText || (isActive ? 'Delete' : 'Restore');
  const message =
    confirmMessage ||
    `Are you sure you want to ${actionLabel.toLowerCase()} these ${name}?`;

  return (
    <EntityActionDeleteComponent
      entityName={firstEntity.entityName}
      id={id}
      message={message}
      header={header || `${actionLabel} ${capitalize(name)}`}
      isActive={isActive}
      confirmButtonContent={confirmButtonContent || actionLabel}
      actionComponentProps={{
        type: 'children',
        children: ({ onClick }) => (
          <ActionComponentBulk
            dataCy="components/Entity/Action/Delete/Bulk:delete"
            entitiesWithPermissionCount={
              entitiesWithPermission.length
            }
            entitiesSelectedCount={entities.length}
            content={actionComponentProps?.text || actionLabel}
            icon={
              actionComponentProps?.icon ||
              (isActive ? 'trash' : 'undo')
            }
            onClick={onClick}
          />
        ),
      }}
      options={options}
    />
  );
};
